import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import style from './zone.module.css';
import classNames from 'classnames';
import { useZone } from 'hooks/useZones.js';
import zoneContext from 'contexts/zone.js';
import scopeLogger from '#/lib/scope-logger.js';

export { DrawingZone };

const console = scopeLogger('RMI:DrawingZone');

/**
 *
 * @param props
 */
function DrawingZone(props) {
  const [ready, setReady] = useState(false);
  /**
   *
   * @type {React.MutableRefObject<HTMLDivElement>}
   */
  const containerRef = useRef();
  const { enabled, zoneStyle, getElementPosition } = useZone(
    props.zone_id,
    containerRef.current,
  );

  useLayoutEffect(() => {
    if (props.onSizeChanged) {
      props.onSizeChanged(containerRef.current, zoneStyle);
    }
    // call this effect only on styleObject change, onSizeChanged will be relevant at that moment
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoneStyle]);

  const className = useMemo(
    () => classNames(style.zone, props.className),
    [props.className],
  );

  /**
   * Render all child component (that rely on zone size) only after mounting that element into DOM.
   */
  useEffect(() => {
    setReady(true);
  }, []);

  useEffect(() => {
    if(ready) {
      props.onReady?.(containerRef.current);
    }
  }, [props, ready]);

  const updatePositions = useCallback(
    (reason) => {
      console.log(`iterate over ${elements.current.size} elements to update positions. reason: ${reason}`,);

      elements.current.forEach((cb, element) => {
        getElementPosition(element).then((position) => {
          if (elements.current.has(element)) {
            console.log('Element still registered');
            cb(position);
          } else {
            console.log('Too late, element is not registered');
          }
        });
      });
    },
    [getElementPosition],
  );

  /**
   * @type {React.MutableRefObject<Number>}
   */
  const raf = useRef();

  const catchTransition = useCallback(
    (ev) => {
      console.log('transition end', ev.propertyName);
      /*
       * handle only one of them
       */
      cancelAnimationFrame(raf.current);
      raf.current = requestAnimationFrame(() => {
        updatePositions(`RAF: on transition end`);
      });
    },
    [updatePositions],
  );

  useEffect(() => {
    const onResize = (_ev) => {
      updatePositions('resize event');
    };
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [updatePositions]);

  useEffect(() => {
    updatePositions('zone changed');
  }, [zoneStyle, updatePositions]);
  const elements = useRef(new Map());

  const registerElement = useCallback(
    (element, cb) => {
      console.log('register new element', element);
      elements.current.set(element, cb);
      getElementPosition(element)
        .then((position) => {
          console.log('got position for element', element, position);
          return position;
        })
        .then(cb);

      return () => {
        console.log('un-register element', element);

        elements.current.delete(element);
      };
    },
    [getElementPosition],
  );

  const ctxValue = useMemo(
    () => ({
      zone_id: props.zone_id,
      registerElement,
    }),
    [props.zone_id, registerElement],
  );

  if (!zoneStyle) {
    return null;
  }

  return (
    <div
      className={className}
      style={zoneStyle}
      ref={containerRef}
      onTransitionEnd={catchTransition}
      data-role="drawing-zone"
      data-enabled={enabled}
    >
      <zoneContext.Provider value={ctxValue}>
        {ready ? props.children : null}
      </zoneContext.Provider>
    </div>
  );
}
